import runTransaction from "../../utils/runTransaction";

import agreementStatuses from "../agreements/constants/agreementStatuses";
import createCommentData from "../discussion/createCommentData";
import createDiscussionChannelData from "../discussion/createDiscussionChannelData";
import { DiscussionChannel } from "../discussion/types/DiscussionChannel";
import createReaderInquiryDraftTodoData from "../todos/data/offers/createReaderInquiryDraftTodoData";

import offerStatuses from "./constants/offerStatuses";
import offerTypes from "./constants/offerTypes";
import createOfferData from "./createOfferData";
import getCommentSender from "./utils/getCommentSender";
import getOffersToCancel from "./utils/getOffersToCancel";

export default async function createOffer(firebase, { production, agreement, ...data }) {
	const db = firebase.firestore();

	// Create the offer data
	const offer = createOfferData(firebase, { agreement, production, ...data });

	// Get all offers to cancel
	const offersToCancel = await getOffersToCancel(db, offer);

	const userRef = offer.secondParty?.type === "user" ? offer.secondParty.ref : null;

	// Create a discussion channel unique to the production + recipient
	const discussionChannelRef =
		offer.secondParty?.type === "user"
			? db.collection("discussionChannels").doc(`${offer.production.id}-${offer.secondParty.id}`)
			: null;

	await runTransaction(firebase, [userRef, discussionChannelRef], (transaction, [user, discussionChannel]) => {
		// Create agreement if it doesn't exist
		if (agreement?.status === agreementStatuses.PENDING_APPROVAL) {
			transaction.set(agreement.ref, agreement);
		}

		// Cancel other inquiries if this is a work order
		if (offer.type === offerTypes.WORK_ORDER && offersToCancel.length > 0) {
			for (const cancelOffer of offersToCancel) {
				transaction.update(cancelOffer.ref, {
					status: offerStatuses.CANCELLED,
				});
			}
		}

		if (user && !discussionChannel?.exists && offer.status !== offerStatuses.DRAFT) {
			discussionChannel = createDiscussionChannelData(firebase, {
				ref: discussionChannelRef,
				name: offer.secondParty.name,
				channels: [`offers/${offer.id}`],
				producerId: offer.production.producer,
				users: offer.discussionVisibleForPublisher
					? [offer.createdBy, user, ...production.publisherManagers]
					: [offer.createdBy, user],
				subject: {
					...offer.production,
					name: offer.production.title,
					language: offer.production.language,
				},
				seenByUserIds: [offer.createdBy.id],
				createdBy: offer.createdBy,
			});

			transaction.set(discussionChannel.ref, discussionChannel);
		}

		// Create discussion thread
		if (offer.optionalMessage && offer.status !== offerStatuses.DRAFT) {
			const comment = createCommentData(firebase, {
				text: offer.optionalMessage,
				channels: [discussionChannel.id, `offers/${offer.id}`],
				createdBy: offer.createdBy,
				sender: getCommentSender(offer),
			});

			transaction.update(
				discussionChannel.ref,
				DiscussionChannel.partial().parse({
					lastComment: comment,
				}),
			);

			transaction.set(comment.ref, comment);
		}

		if (offer.status === offerStatuses.DRAFT) {
			const todo = createReaderInquiryDraftTodoData(firebase, { offer, status: offerStatuses.DRAFT });

			transaction.set(todo.ref, todo);
		}

		// Create the offer
		transaction.set(offer.ref, offer);
	});

	return offer;
}
